// pages/admin/AdminHappyHours.jsx — configuration des Happy Hours automatiques.
// Le prix bascule tout seul selon le jour/l'heure côté POS (voir POSPage.jsx) :
// plus besoin de deux touches caisse séparées "Produit" / "Produit HH".
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../utils/auth';
import { happyHoursApi, stock as stockApi, sites as sitesApi } from '../../utils/api';
import { Plus, Edit2, Trash2, Zap, Tag } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ConfirmModal';

// Convention PostgreSQL EXTRACT(DOW) : 0=dimanche … 6=samedi
const DAYS = [
  { value:1, label:'Lun' }, { value:2, label:'Mar' }, { value:3, label:'Mer' },
  { value:4, label:'Jeu' }, { value:5, label:'Ven' }, { value:6, label:'Sam' },
  { value:0, label:'Dim' },
];

function summarizeDays(daysOfWeek) {
  if (!daysOfWeek || daysOfWeek.length === 7) return 'Tous les jours';
  const set = new Set(daysOfWeek);
  const weekdays = [1,2,3,4,5];
  if (weekdays.every(d => set.has(d)) && daysOfWeek.length === 5) return 'Lun-Ven';
  return DAYS.filter(d => set.has(d.value)).map(d => d.label).join(', ');
}

// ── Modal création / édition de la règle ────────────────────────
function HappyHourModal({ hh, sites, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:      hh?.name || '',
    siteId:    hh?.site_id || '',
    daysOfWeek: hh?.days_of_week || [1,2,3,4,5,6,0],
    startTime: hh?.start_time?.slice(0,5) || '17:00',
    endTime:   hh?.end_time?.slice(0,5) || '19:00',
  });
  const [saving, setSaving] = useState(false);

  const toggleDay = (v) => setForm(p => ({
    ...p,
    daysOfWeek: p.daysOfWeek.includes(v) ? p.daysOfWeek.filter(d => d !== v) : [...p.daysOfWeek, v],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.daysOfWeek.length === 0) return toast.error('Sélectionnez au moins un jour');
    setSaving(true);
    try {
      const payload = { ...form, siteId: form.siteId || null };
      if (hh) { await happyHoursApi.update(hh.id, payload); toast.success('Happy hour mise à jour'); }
      else    { await happyHoursApi.create(payload); toast.success('Happy hour créée'); }
      onSaved(); onClose();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{ maxWidth:440 }}>
        <h3 className="modal-title">{hh ? 'Modifier' : 'Nouvelle'} Happy Hour</h3>
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">Nom *</label>
            <input className="input" required value={form.name}
              onChange={e=>setForm(p=>({...p,name:e.target.value}))}
              placeholder="Ex: Happy Hour Bières"/>
          </div>

          <div className="form-group">
            <label className="form-label">Site</label>
            <select className="input" value={form.siteId}
              onChange={e=>setForm(p=>({...p,siteId:e.target.value}))}>
              <option value="">Tous les sites</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Jours</label>
            <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
              {DAYS.map(d => {
                const active = form.daysOfWeek.includes(d.value);
                return (
                  <button key={d.value} type="button" onClick={() => toggleDay(d.value)}
                    style={{
                      padding:'6px 12px', borderRadius:8, cursor:'pointer', fontFamily:'inherit',
                      border:`1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                      background: active ? 'rgba(124,92,252,0.15)' : 'var(--bg-elevated)',
                      color: active ? 'var(--accent-light)' : 'var(--text-3)',
                      fontSize:12, fontWeight:700,
                    }}>
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Heure de début</label>
              <input type="time" className="input" required value={form.startTime}
                onChange={e=>setForm(p=>({...p,startTime:e.target.value}))}/>
            </div>
            <div className="form-group">
              <label className="form-label">Heure de fin</label>
              <input type="time" className="input" required value={form.endTime}
                onChange={e=>setForm(p=>({...p,endTime:e.target.value}))}/>
            </div>
          </div>
          {form.endTime && form.startTime && form.endTime < form.startTime && (
            <p style={{ fontSize:11, color:'var(--text-3)', marginTop:-8, marginBottom:12 }}>
              La plage traverse minuit ({form.startTime} → {form.endTime} le lendemain).
            </p>
          )}

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="spinner" style={{width:14,height:14}}/> : (hh ? 'Mettre à jour' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal tarifs produits pour une happy hour ────────────────────
function PricesModal({ hh, onClose }) {
  const [products, setProducts] = useState([]);
  const [prices, setPrices]     = useState({}); // { productId: '2.50' }
  const [saving, setSaving]     = useState(false);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([stockApi.list(), happyHoursApi.prices(hh.id)])
      .then(([prodR, priceR]) => {
        setProducts((prodR.data.products || []).filter(p => !p.is_combo));
        const map = {};
        for (const p of priceR.data.prices || []) map[p.productId] = String(p.price);
        setPrices(map);
      })
      .finally(() => setLoading(false));
  }, [hh.id]);

  const setPrice = (productId, v) => setPrices(prev => ({ ...prev, [productId]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(prices)
        .filter(([, v]) => v !== '' && v !== undefined && v !== null)
        .map(([productId, price]) => ({ productId, price }));
      await happyHoursApi.setPrices(hh.id, payload);
      toast.success('Tarifs mis à jour');
      onClose();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{ maxWidth:520 }}>
        <h3 className="modal-title">Tarifs — {hh.name}</h3>
        <p style={{ fontSize:12, color:'var(--text-3)', marginTop:-8, marginBottom:14 }}>
          Laissez le prix vide pour qu'un produit ne soit pas concerné par cette happy hour.
        </p>
        {loading ? (
          <div style={{ textAlign:'center', padding:30 }}><span className="spinner"/></div>
        ) : (
          <div style={{ maxHeight:400, overflowY:'auto', display:'flex', flexDirection:'column', gap:6 }}>
            {products.map(p => {
              const base = parseFloat(p.sell_price || p.price || 0);
              return (
                <div key={p.id} style={{ display:'flex', alignItems:'center', gap:10,
                  padding:'6px 10px', background:'var(--bg-elevated)', borderRadius:8 }}>
                  <span style={{ fontSize:16 }}>{p.icon || '📦'}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {p.name} {p.unit_label && <span style={{ color:'var(--text-3)', fontWeight:400 }}>{p.unit_label}</span>}
                    </div>
                    <div style={{ fontSize:11, color:'var(--text-3)' }}>Prix normal : {base.toFixed(2)} €</div>
                  </div>
                  <input type="number" step="0.01" min="0" className="input" style={{ width:90 }}
                    placeholder="—" value={prices[p.id] ?? ''}
                    onChange={e => setPrice(p.id, e.target.value)}/>
                </div>
              );
            })}
          </div>
        )}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" disabled={saving || loading} onClick={save}>
            {saving ? <span className="spinner" style={{width:14,height:14}}/> : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// `embedded` : rendu comme onglet interne (ex: dans Mode caisse).
export default function AdminHappyHours({ embedded = false }) {
  const { can } = useAuth();
  const [rules, setRules]     = useState([]);
  const [sites, setSitesList] = useState([]);
  const [modal, setModal]     = useState(null);   // 'new' | rule object
  const [pricesFor, setPricesFor] = useState(null); // rule object
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(() => {
    happyHoursApi.list().then(r => setRules(r.data.happyHours || []));
    sitesApi.list().then(r => setSitesList(r.data.sites || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!can('admin')) return (
    <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>
  );

  const remove = async () => {
    setRemoving(true);
    try {
      await happyHoursApi.remove(confirmRemove.id);
      toast.success('Supprimée');
      setConfirmRemove(null);
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setRemoving(false); }
  };

  const newHappyHourBtn = (
    <button className="btn btn-primary" onClick={() => setModal('new')}>
      <Plus size={15}/> Nouvelle Happy Hour
    </button>
  );

  return (
    <>
      {embedded ? (
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>{newHappyHourBtn}</div>
      ) : (
        <div className="page-header">
          <div>
            <h2>Happy Hours</h2>
            <p>Le prix bascule automatiquement au POS selon le jour/l'heure</p>
          </div>
          {newHappyHourBtn}
        </div>
      )}

      <div className={embedded ? undefined : 'page-body'}>
        {rules.length === 0 ? (
          <div className="empty-state card">
            <p>Aucune Happy Hour configurée</p>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:14 }}>
            {rules.map(hh => (
              <div key={hh.id} className="card"
                style={{ borderLeft: `4px solid ${hh.is_active_now ? 'var(--teal)' : 'var(--border)'}` }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:10 }}>
                  <div style={{ width:36, height:36, borderRadius:10, flexShrink:0,
                    background: hh.is_active_now ? 'rgba(0,210,180,0.15)' : 'var(--bg-elevated)',
                    display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <Zap size={16} color={hh.is_active_now ? 'var(--teal)' : 'var(--text-3)'}/>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <h3 style={{ fontSize:15, fontWeight:700, margin:0 }}>{hh.name}</h3>
                      {hh.is_active_now && (
                        <span style={{ fontSize:9, fontWeight:800, color:'#fff', background:'var(--teal)',
                          padding:'2px 7px', borderRadius:99 }}>EN COURS</span>
                      )}
                      {!hh.is_active && (
                        <span style={{ fontSize:9, fontWeight:700, color:'var(--text-3)', background:'var(--bg-elevated)',
                          padding:'2px 7px', borderRadius:99 }}>DÉSACTIVÉE</span>
                      )}
                    </div>
                    <p style={{ fontSize:12, color:'var(--text-3)', marginTop:2 }}>
                      {hh.site_name || 'Tous les sites'} · {summarizeDays(hh.days_of_week)} · {hh.start_time?.slice(0,5)}–{hh.end_time?.slice(0,5)}
                    </p>
                  </div>
                  <div style={{ display:'flex', gap:4 }}>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setModal(hh)}><Edit2 size={13}/></button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setConfirmRemove(hh)}><Trash2 size={13} color="var(--red)"/></button>
                  </div>
                </div>
                <button className="btn btn-secondary btn-sm" style={{ width:'100%', justifyContent:'center' }}
                  onClick={() => setPricesFor(hh)}>
                  <Tag size={13}/> {hh.product_count} produit{hh.product_count>1?'s':''} — Gérer les tarifs
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <HappyHourModal hh={modal==='new'?null:modal} sites={sites}
          onClose={()=>setModal(null)} onSaved={load}/>
      )}
      {pricesFor && (
        <PricesModal hh={pricesFor} onClose={()=>{ setPricesFor(null); load(); }}/>
      )}
      {confirmRemove && (
        <ConfirmModal
          title="Supprimer la Happy Hour"
          message={`Supprimer "${confirmRemove.name}" ?`}
          confirmLabel="Supprimer"
          danger
          loading={removing}
          onConfirm={remove}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </>
  );
}
